import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  ChevronLeft,
  ChevronRight,
  FileText,
  Clock,
  Award,
  Loader2,
  Download,
  User,
  Building
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SessionReplay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  const [replayData, setReplayData] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Slide sync timestamps (evenly distributed based on video duration)
  const [slideTimestamps, setSlideTimestamps] = useState([]);

  useEffect(() => {
    fetchReplayData();
  }, [id]);

  const fetchReplayData = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${id}/replay`);
      setReplayData(response.data);
      
      // Fetch user info
      if (response.data.session?.user_id) {
        try {
          const userResponse = await axios.get(`${API}/users/${response.data.session.user_id}`);
          setUser(userResponse.data);
        } catch (err) {
          console.log("Could not fetch user info");
        }
      }
    } catch (error) {
      console.error("Failed to fetch replay data:", error);
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      
      // Auto-sync slide based on video time
      if (slideTimestamps.length > 0 && duration > 0) {
        const currentPercentage = (videoRef.current.currentTime / duration) * 100;
        
        // Find the appropriate slide for current time
        for (let i = slideTimestamps.length - 1; i >= 0; i--) {
          if (currentPercentage >= slideTimestamps[i].percentage) {
            if (currentSlide !== i) {
              setCurrentSlide(i);
            }
            break;
          }
        }
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const videoDuration = videoRef.current.duration;
      setDuration(videoDuration);
      
      // Generate slide timestamps based on video duration
      const totalSlides = replayData?.slides?.slides?.length || 1;
      const timestamps = [];
      for (let i = 0; i < totalSlides; i++) {
        timestamps.push({
          slideIndex: i,
          percentage: (i / totalSlides) * 100,
          startTime: (i / totalSlides) * videoDuration
        });
      }
      setSlideTimestamps(timestamps);
    }
  };

  const handleSeek = (value) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleVolumeChange = (value) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const toggleFullscreen = () => {
    const container = document.getElementById('replay-container');
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    } else if (container) {
      container.requestFullscreen();
      setIsFullscreen(true);
    }
  };

  // Download video
  const downloadVideo = async () => {
    if (replayData?.video_url) {
      try {
        toast.info("Starting download...");
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${replayData.video_url}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `session_${id}_recording.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Download started!");
      } catch (error) {
        console.error("Download failed:", error);
        toast.error("Download failed");
      }
    }
  };

  // Download presentation
  const downloadPresentation = async () => {
    try {
      toast.info("Starting download...");
      const response = await fetch(`${API}/sessions/${id}/download-presentation`);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = replayData?.session?.presentation_file || `presentation_${id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Download started!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Presentation download failed");
    }
  };

  // Go to specific slide and sync video
  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
    
    // If video is available, seek to the corresponding time
    if (videoRef.current && slideTimestamps[slideIndex]) {
      const targetTime = slideTimestamps[slideIndex].startTime;
      videoRef.current.currentTime = targetTime;
      setCurrentTime(targetTime);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const nextSlide = () => {
    if (replayData?.slides?.slides && currentSlide < replayData.slides.slides.length - 1) {
      goToSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!replayData) {
    return (
      <div className="text-center py-12">
        <p className="text-[#5C5C5C]">Session not found</p>
        <Link to="/dashboard">
          <Button className="mt-4">Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const { session, slides, feedback, has_video, video_url } = replayData;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="session-replay-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Session Replay
          </h1>
          <p className="text-[#5C5C5C] mt-1">{session?.topic_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {has_video && (
            <Button variant="outline" onClick={downloadVideo} className="rounded-full" data-testid="download-video-btn">
              <Download className="w-4 h-4 mr-2" />
              Video
            </Button>
          )}
          <Button variant="outline" onClick={downloadPresentation} className="rounded-full" data-testid="download-ppt-btn">
            <FileText className="w-4 h-4 mr-2" />
            Slides
          </Button>
          {feedback && (
            <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
              <Award className="w-5 h-5 text-primary" />
              <span className="font-bold text-primary">{feedback.overall_score}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div id="replay-container" className="grid lg:grid-cols-3 gap-6">
        {/* Video Player */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="stats-card overflow-hidden">
            <CardContent className="p-0">
              {has_video ? (
                <div className="relative bg-black">
                  <video
                    ref={videoRef}
                    src={`${process.env.REACT_APP_BACKEND_URL}${video_url}`}
                    className="w-full aspect-video"
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                    data-testid="replay-video"
                  />
                  
                  {/* Video Controls */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    {/* Progress Bar */}
                    <Slider
                      value={[currentTime]}
                      max={duration || 100}
                      step={0.1}
                      onValueChange={handleSeek}
                      className="mb-4"
                      data-testid="video-progress"
                    />
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => skip(-10)}
                          className="text-white hover:bg-white/20"
                        >
                          <SkipBack className="w-5 h-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={togglePlay}
                          data-testid="play-pause-btn"
                          className="text-white hover:bg-white/20"
                        >
                          {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => skip(10)}
                          className="text-white hover:bg-white/20"
                        >
                          <SkipForward className="w-5 h-5" />
                        </Button>
                        <span className="text-white text-sm font-mono ml-2">
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={toggleMute}
                          className="text-white hover:bg-white/20"
                        >
                          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                        </Button>
                        <div className="w-24">
                          <Slider
                            value={[isMuted ? 0 : volume]}
                            max={1}
                            step={0.1}
                            onValueChange={handleVolumeChange}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={toggleFullscreen}
                          className="text-white hover:bg-white/20"
                        >
                          {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-video bg-zinc-100 flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-[#5C5C5C]">No video recording available</p>
                    <p className="text-sm text-[#9CA3AF]">This session was not recorded</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Session Info */}
          <Card className="stats-card">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{user?.name || 'Unknown User'}</p>
                    <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                      <Building className="w-3 h-3" />
                      {user?.department || 'N/A'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm text-[#5C5C5C]">
                  <div>
                    <p className="text-xs text-[#9CA3AF]">Date</p>
                    <p className="font-medium">
                      {new Date(session?.created_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#9CA3AF]">Duration</p>
                    <p className="font-medium">
                      {duration > 0 ? formatTime(duration) : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#9CA3AF]">Status</p>
                    <p className="font-medium capitalize">{session?.status}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Slides Panel */}
        <div className="space-y-4">
          <Card className="stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Presentation Slides
                </span>
                <span className="text-sm font-normal text-[#5C5C5C]">
                  {currentSlide + 1} / {slides?.total || slides?.slides?.length || 0}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {slides?.slides?.length > 0 ? (
                <div className="space-y-4">
                  {/* Current Slide */}
                  <div className="aspect-video bg-white rounded-lg border-2 border-border overflow-hidden relative">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${slides.slides[currentSlide]}`}
                      alt={`Slide ${currentSlide + 1}`}
                      className="w-full h-full object-contain"
                      data-testid="current-slide"
                    />
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-primary/90 rounded-full text-xs text-white font-medium">
                      {currentSlide + 1} / {slides.total}
                    </div>
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={prevSlide}
                      disabled={currentSlide === 0}
                      className="rounded-full"
                      data-testid="prev-slide-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-[#5C5C5C]">Click to sync video</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={nextSlide}
                      disabled={currentSlide === slides.slides.length - 1}
                      className="rounded-full"
                      data-testid="next-slide-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Thumbnails */}
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {slides.slides.map((slide, idx) => (
                      <button
                        key={idx}
                        onClick={() => goToSlide(idx)}
                        className={`aspect-video rounded overflow-hidden border-2 transition-all ${
                          idx === currentSlide ? 'border-primary ring-2 ring-primary/20' : 'border-border opacity-60 hover:opacity-100'
                        }`}
                        data-testid={`slide-thumb-${idx}`}
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${slide}`}
                          alt={`Thumb ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-[#5C5C5C]">No slides available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feedback Summary */}
          {feedback && (
            <Card className="stats-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold">Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-primary/5 rounded-lg">
                    <p className="text-xl font-bold text-primary">{feedback.content_clarity || '-'}</p>
                    <p className="text-xs text-[#5C5C5C]">Clarity</p>
                  </div>
                  <div className="text-center p-3 bg-[#059669]/5 rounded-lg">
                    <p className="text-xl font-bold text-[#059669]">{feedback.confidence_presence || '-'}</p>
                    <p className="text-xs text-[#5C5C5C]">Confidence</p>
                  </div>
                  <div className="text-center p-3 bg-[#2563EB]/5 rounded-lg">
                    <p className="text-xl font-bold text-[#2563EB]">{feedback.engagement_level || '-'}</p>
                    <p className="text-xs text-[#5C5C5C]">Engagement</p>
                  </div>
                  <div className="text-center p-3 bg-[#D97706]/5 rounded-lg">
                    <p className="text-xl font-bold text-[#D97706]">{feedback.qa_handling || '-'}</p>
                    <p className="text-xs text-[#5C5C5C]">Q&A</p>
                  </div>
                </div>
                <Link to={`/session/${id}/feedback`}>
                  <Button className="w-full bg-primary hover:bg-primary/90 rounded-full mt-2">
                    <Award className="w-4 h-4 mr-2" />
                    View Full Feedback
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
